/**
 * ChatMessages — Chat zone of the unified Activity Bar surface.
 *
 * Pure chat thread: user/assistant messages only. No action cards,
 * no system messages, no greeting. Status messages are handled by
 * ActivityBarHeader's transient status display.
 *
 * States:
 *   - Empty (no thread): Minimal — only offline banner if needed
 *   - Active thread: User bubbles (right), assistant bubbles (left), timestamps
 *   - Loading: Typing indicator (animated dots)
 *   - Error: Inline error banner with retry
 *   - Offline: Banner, input disabled
 *
 * Phase 7: Simplified — action cards removed (hero buttons handle CTAs),
 * system messages removed (transient header status handles those),
 * chat presets moved to InputRow.
 *
 * See: docs/features/infrastructure/app-shell.md
 */
import React, { useRef, useEffect } from "react";
import { Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import SearchResults from "./SearchResults";

const ALLOWED_ELEMENTS = ["p", "strong", "em", "ul", "ol", "li", "a", "br"];

/**
 * @param {Object} props
 * @param {Array} props.messages - Array of { id, role, content, timestamp, actionChips? }
 *   role: 'user' | 'assistant'
 * @param {boolean} props.isLoading - Show typing indicator
 * @param {string|null} props.error - Error message (null = no error)
 * @param {Function} props.onRetry - Retry handler for errors
 * @param {boolean} props.isOffline - Network offline
 * @param {Function} props.onActionChip - Handler for inline action chips in messages
 */
const ChatMessages = ({
    messages = [],
    isLoading = false,
    error = null,
    onRetry,
    isOffline = false,
    onActionChip,
}) => {
    const scrollRef = useRef(null);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages.length, isLoading]);

    // Format relative time
    const formatRelativeTime = (timestamp) => {
        if (!timestamp) return "";
        const now = Date.now();
        const diff = Math.floor((now - timestamp) / 1000);
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return new Date(timestamp).toLocaleDateString();
    };

    // Filter to only user and assistant messages (no system messages)
    const chatMessages = messages.filter((msg) => msg.role === "user" || msg.role === "assistant");

    // Empty state — no messages yet: collapse to minimal or show nothing
    if (chatMessages.length === 0 && !isLoading && !error) {
        if (isOffline) {
            return (
                <div className="chat-messages chat-messages-empty" ref={scrollRef}>
                    <div className="chat-messages-offline-banner">
                        Chat requires an internet connection.
                    </div>
                </div>
            );
        }
        // Collapse entirely — hero buttons are the CTA, not the chat area
        return null;
    }

    return (
        <div className="chat-messages" ref={scrollRef}>
            {/* Offline banner */}
            {isOffline && (
                <div className="chat-messages-offline-banner">
                    Chat requires an internet connection.
                </div>
            )}

            {/* Message thread */}
            {chatMessages.map((msg) => (
                <div key={msg.id} className={`chat-message chat-message-${msg.role}`}>
                    <div className="chat-message-bubble">
                        {msg.role === "assistant" ? (
                            <div className="chat-message-row">
                                <span className="chat-message-ai-icon" title="AI Assistant">
                                    <Sparkles size={12} />
                                </span>
                                <div className="chat-message-content chat-markdown">
                                    <ReactMarkdown
                                        allowedElements={ALLOWED_ELEMENTS}
                                        unwrapDisallowed={true}
                                        components={{
                                            a: ({ href, children }) => (
                                                <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                                            ),
                                        }}
                                    >
                                        {msg.content}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        ) : (
                            <span className="chat-message-content">{msg.content}</span>
                        )}
                        {/* Search result mini-cards (search_patients function) */}
                        {msg.searchResults?.length > 0 && (
                            <SearchResults
                                results={msg.searchResults}
                            />
                        )}
                        {/* Inline action chips (assistant messages only) */}
                        {msg.actionChips?.length > 0 && (
                            <div className="chat-message-action-chips">
                                {msg.actionChips.map((chip) => (
                                    <button
                                        key={chip.id}
                                        className="chat-message-action-chip"
                                        onClick={() => onActionChip?.(chip)}
                                    >
                                        {chip.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <span className="chat-message-timestamp" title={msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ""}>
                        {formatRelativeTime(msg.timestamp)}
                    </span>
                </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
                <div className="chat-message chat-message-assistant">
                    <div className="chat-message-bubble chat-message-typing">
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                    </div>
                </div>
            )}

            {/* Error banner */}
            {error && (
                <div className="chat-messages-error">
                    <span>{error}</span>
                    {onRetry && (
                        <button className="chat-messages-retry-btn" onClick={onRetry}>
                            Try again
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChatMessages;
